// src/features/project/charts.tsx
// Hand-rolled SVG charts for project analytics — no chart library, no new deps.
// Colours are semantic Tailwind tokens (fill-*/stroke-*) so both themes and all
// presets track automatically. Each chart is role="img" with an aria summary and
// scrolls inside its own container on narrow screens.

import type { BurndownPoint, VelocityPoint, CumulativeFlowPoint } from '@core/types/project';

const W = 640;
const H = 240;
const PAD = { top: 16, right: 16, bottom: 32, left: 40 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

// Palette used for the variable-length cumulative-flow series. Each entry is a
// semantic token class; the set cycles for boards with many columns.
const SERIES_FILL = [
  'fill-primary',
  'fill-accent',
  'fill-muted-foreground',
  'fill-destructive',
  'fill-secondary',
];
const SERIES_SWATCH = [
  'bg-primary',
  'bg-accent',
  'bg-muted-foreground',
  'bg-destructive',
  'bg-secondary',
];

function niceMax(v: number): number {
  if (v <= 0) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

function ChartFrame({
  title,
  ariaLabel,
  yMax,
  xLabels,
  children,
}: {
  title: string;
  ariaLabel: string;
  yMax: number;
  xLabels: string[];
  children: React.ReactNode;
}) {
  const ticks = 4;
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full min-w-[420px]"
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="xMidYMid meet"
      >
        <title>{title}</title>
        {/* Y gridlines + labels */}
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const y = PAD.top + (PLOT_H * i) / ticks;
          const val = Math.round(yMax - (yMax * i) / ticks);
          return (
            <g key={i}>
              <line
                x1={PAD.left}
                y1={y}
                x2={W - PAD.right}
                y2={y}
                className="stroke-border"
                strokeWidth={1}
                strokeDasharray={i === ticks ? undefined : '2 3'}
              />
              <text
                x={PAD.left - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-muted-foreground"
                fontSize={10}
              >
                {val}
              </text>
            </g>
          );
        })}
        {/* X labels */}
        {xLabels.map((lbl, i) => {
          const x =
            xLabels.length === 1
              ? PAD.left + PLOT_W / 2
              : PAD.left + (PLOT_W * i) / (xLabels.length - 1);
          return (
            <text
              key={i}
              x={x}
              y={H - PAD.bottom + 16}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={10}
            >
              {lbl}
            </text>
          );
        })}
        {children}
      </svg>
    </div>
  );
}

function Legend({ items }: { items: { label: string; swatch: string; dashed?: boolean }[] }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-4">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className={`inline-block h-2.5 w-2.5 rounded-sm ${it.swatch} ${it.dashed ? 'opacity-60' : ''}`}
            aria-hidden="true"
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Burndown — ideal vs actual remaining points over the sprint days
// ---------------------------------------------------------------------------

export function BurndownChart({
  points,
  totalPoints,
  labels,
}: {
  points: BurndownPoint[];
  totalPoints: number;
  labels: { title: string; ideal: string; remaining: string; empty: string };
}) {
  if (!points || points.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{labels.empty}</p>;
  }
  const yMax = niceMax(Math.max(totalPoints, ...points.map((p) => p.remaining_points ?? 0)));
  const n = points.length;
  const x = (i: number) => (n === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (PLOT_W * i) / (n - 1));
  const y = (v: number) => PAD.top + PLOT_H - (PLOT_H * Math.max(0, v)) / yMax;

  const ideal = points.map((p, i) => `${x(i)},${y(p.ideal_remaining ?? 0)}`).join(' ');
  const actual = points.map((p, i) => `${x(i)},${y(p.remaining_points ?? 0)}`).join(' ');
  const xLabels = points.map((p) =>
    p.date ? new Date(p.date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '',
  );

  return (
    <div>
      <ChartFrame title={labels.title} ariaLabel={labels.title} yMax={yMax} xLabels={xLabels}>
        <polyline
          points={ideal}
          fill="none"
          className="stroke-muted-foreground"
          strokeWidth={1.5}
          strokeDasharray="4 4"
        />
        <polyline points={actual} fill="none" className="stroke-primary" strokeWidth={2} />
        {points.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.remaining_points ?? 0)} r={2.5} className="fill-primary" />
        ))}
      </ChartFrame>
      <Legend
        items={[
          { label: labels.remaining, swatch: 'bg-primary' },
          { label: labels.ideal, swatch: 'bg-muted-foreground', dashed: true },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Velocity — committed vs completed grouped bars per sprint + average line
// ---------------------------------------------------------------------------

export function VelocityChart({
  sprints,
  averageVelocity,
  labels,
}: {
  sprints: VelocityPoint[];
  averageVelocity: number;
  labels: { title: string; committed: string; completed: string; average: string; empty: string };
}) {
  if (!sprints || sprints.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{labels.empty}</p>;
  }
  const yMax = niceMax(
    Math.max(1, ...sprints.map((s) => Math.max(s.committed_points ?? 0, s.completed_points ?? 0))),
  );
  const n = sprints.length;
  const groupW = PLOT_W / n;
  const barW = Math.min(28, (groupW - 8) / 2);
  const y = (v: number) => PAD.top + PLOT_H - (PLOT_H * Math.max(0, v)) / yMax;
  const xLabels = sprints.map((s, i) => s.sprint_name || `#${i + 1}`);
  const avgY = y(averageVelocity);

  return (
    <div>
      <ChartFrame title={labels.title} ariaLabel={labels.title} yMax={yMax} xLabels={xLabels}>
        {sprints.map((s, i) => {
          const cx = PAD.left + groupW * i + groupW / 2;
          const committedH = PAD.top + PLOT_H - y(s.committed_points ?? 0);
          const completedH = PAD.top + PLOT_H - y(s.completed_points ?? 0);
          return (
            <g key={i}>
              <rect
                x={cx - barW - 1}
                y={y(s.committed_points ?? 0)}
                width={barW}
                height={committedH}
                rx={2}
                className="fill-muted"
              />
              <rect
                x={cx + 1}
                y={y(s.completed_points ?? 0)}
                width={barW}
                height={completedH}
                rx={2}
                className="fill-primary"
              />
            </g>
          );
        })}
        {averageVelocity > 0 && (
          <line
            x1={PAD.left}
            y1={avgY}
            x2={W - PAD.right}
            y2={avgY}
            className="stroke-accent"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
        )}
      </ChartFrame>
      <Legend
        items={[
          { label: labels.committed, swatch: 'bg-muted' },
          { label: labels.completed, swatch: 'bg-primary' },
          { label: `${labels.average} (${Math.round(averageVelocity)})`, swatch: 'bg-accent', dashed: true },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cumulative flow — stacked areas per column over time
// ---------------------------------------------------------------------------

export function CumulativeFlowChart({
  points,
  columns,
  labels,
}: {
  points: CumulativeFlowPoint[];
  columns: string[];
  labels: { title: string; empty: string };
}) {
  if (!points || points.length === 0 || columns.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{labels.empty}</p>;
  }
  const n = points.length;
  // Cumulative total per point for the y-scale.
  const totals = points.map((p) =>
    columns.reduce((sum, c) => sum + (p.counts_by_column?.[c] ?? 0), 0),
  );
  const yMax = niceMax(Math.max(1, ...totals));
  const x = (i: number) => (n === 1 ? PAD.left + PLOT_W / 2 : PAD.left + (PLOT_W * i) / (n - 1));
  const y = (v: number) => PAD.top + PLOT_H - (PLOT_H * Math.max(0, v)) / yMax;
  const xLabels = points.map((p) =>
    p.date ? new Date(p.date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : '',
  );

  // Build stacked bands bottom-up.
  const bands = columns.map((col, ci) => {
    const lower: number[] = [];
    const upper: number[] = [];
    points.forEach((p, pi) => {
      const base = columns
        .slice(0, ci)
        .reduce((sum, c) => sum + (p.counts_by_column?.[c] ?? 0), 0);
      lower[pi] = base;
      upper[pi] = base + (p.counts_by_column?.[col] ?? 0);
    });
    const top = points.map((_, i) => `${x(i)},${y(upper[i])}`);
    const bottom = points.map((_, i) => `${x(i)},${y(lower[i])}`).reverse();
    return { col, path: `${top.join(' ')} ${bottom.join(' ')}`, fill: SERIES_FILL[ci % SERIES_FILL.length] };
  });

  return (
    <div>
      <ChartFrame title={labels.title} ariaLabel={labels.title} yMax={yMax} xLabels={xLabels}>
        {bands.map((b) => (
          <polygon key={b.col} points={b.path} className={`${b.fill} opacity-70`} />
        ))}
      </ChartFrame>
      <Legend
        items={columns.map((c, i) => ({ label: c, swatch: SERIES_SWATCH[i % SERIES_SWATCH.length] }))}
      />
    </div>
  );
}
